import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

/**
 * "Verify fitment before shipping" option in the cart (snippets/wd-fit-verify.liquid).
 * Stores the choice, vehicle and VIN as cart attributes so they arrive on the order.
 * While ticked, both are required: Check out is held back (with a message) until they're valid.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} fields
 * @property {HTMLInputElement} vehicle
 * @property {HTMLInputElement} vin
 * @property {HTMLElement} vinError
 * @property {HTMLElement} vehicleError
 * @property {HTMLElement} blockNote
 *
 * @extends {Component<Refs>}
 */
class WdFitVerify extends Component {
  requiredRefs = ['checkbox', 'fields', 'vehicle', 'vin', 'vinError', 'vehicleError', 'blockNote'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Shows or hides the vehicle fields and saves the choice.
   * @param {Event} event - The checkbox change event.
   */
  handleToggle = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const on = event.target.checked;
    if (on && !this.refs.vehicle.value) this.#prefillVehicle();
    this.#sync();
    this.#save();
    if (on) (this.refs.vehicle.value ? this.refs.vin : this.refs.vehicle).focus();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener('click', this.#guardCheckout, true);
  }

  /** The cart drawer re-renders this element; keep the fields in step with the checkbox (it can stay ticked). */
  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  get #vin() {
    return this.refs.vin.value.trim();
  }

  get #complete() {
    return !this.refs.checkbox.checked || (this.refs.vehicle.value.trim().length >= 6 && VIN_PATTERN.test(this.#vin));
  }

  /** Shows the fields when ticked and marks the box incomplete (dims express checkout) until both are valid. */
  #sync() {
    const on = this.refs.checkbox.checked;
    this.refs.fields.hidden = !on;
    this.toggleAttribute('data-incomplete', !this.#complete);
    if (this.#complete) {
      this.refs.blockNote.hidden = true;
      this.#showErrors(false);
    }
  }

  #showErrors(show) {
    const vehicleBad = show && this.refs.vehicle.value.trim().length < 6;
    const vinBad = show && !VIN_PATTERN.test(this.#vin);
    this.refs.vehicleError.hidden = !vehicleBad;
    this.refs.vehicle.classList.toggle('is-invalid', vehicleBad);
    this.refs.vin.classList.toggle('is-invalid', vinBad);
    if (show) this.refs.vinError.hidden = !vinBad;
  }

  /**
   * Holds back Check out (cart page and drawer) while the fitment details are missing.
   * @param {MouseEvent} event
   */
  #guardCheckout = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[name="checkout"]') : null;
    if (!button || this.#complete || !this.isConnected || !this.getClientRects().length) return;
    event.preventDefault();
    event.stopPropagation();
    this.refs.blockNote.hidden = false;
    this.#showErrors(true);
    (this.refs.vehicle.value.trim().length < 6 ? this.refs.vehicle : this.refs.vin).focus();
  };

  /**
   * Saves vehicle and VIN edits.
   * @param {Event} event - The input event.
   */
  handleInput = (event) => {
    if (event.target === this.refs.vin) {
      const vin = this.refs.vin.value.toUpperCase().replace(/\s/g, '');
      if (vin !== this.refs.vin.value) this.refs.vin.value = vin;
      this.refs.vinError.hidden = vin.length === 0 || VIN_PATTERN.test(vin) || vin.length < 17;
      if (VIN_PATTERN.test(vin)) this.refs.vin.classList.remove('is-invalid');
    }
    if (this.refs.vehicle.value.trim().length >= 6) {
      this.refs.vehicleError.hidden = true;
      this.refs.vehicle.classList.remove('is-invalid');
    }
    this.#sync();
    this.#saveDebounced();
  };

  /** Fills the vehicle from the homepage vehicle search, when the shopper picked one. */
  #prefillVehicle() {
    try {
      const saved = JSON.parse(localStorage.getItem('wd-vehicle') || 'null');
      if (saved?.year && saved?.make && saved?.model) this.refs.vehicle.value = `${saved.year} ${saved.make} ${saved.model}`;
    } catch (e) {}
  }

  #saveDebounced = debounce(() => this.#save(), 400);

  async #save() {
    const on = this.refs.checkbox.checked;
    // Empty values remove the attribute, so unticking leaves nothing on the order.
    const attributes = {
      'Verify fitment': on ? 'Yes' : '',
      Vehicle: on ? this.refs.vehicle.value.trim() : '',
      VIN: on ? this.refs.vin.value.trim() : '',
    };

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    try {
      await fetch(Theme.routes.cart_update_url, { ...fetchConfig('json', { body: JSON.stringify({ attributes }) }), signal: abortController.signal });
    } catch (error) {
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#guardCheckout, true);
    this.#activeFetch?.abort();
  }
}

if (!customElements.get('wd-fit-verify')) {
  customElements.define('wd-fit-verify', WdFitVerify);
}
